import type { DashEnergyWidgetConfig, DashEnergyWidgetTypes } from './dash-energy-types';

export type EnergyWidgetGridSize = 'full' | 'half' | 'quarter';
export type EnergyWidgetConfigurationKind =
	| 'info'
	| 'live'
	| 'period'
	| 'ranking'
	| 'mix'
	| 'room'
	| 'flow'
	| 'heatmap'
	| 'peak'
	| 'device'
	| 'rooms'
	| 'basic';

export type EnergyWidgetRegistryEntry<T extends DashEnergyWidgetTypes> = {
	titleKey: string;
	titleDefault: string;
	defaultConfig: DashEnergyWidgetConfig<T>;
	grid: EnergyWidgetGridSize;
	configuration: EnergyWidgetConfigurationKind;
	supportsPeriod?: true;
	settings?: {
		titleKey: string;
		titleDefault: string;
		descriptionKey: string;
		descriptionDefault: string;
		icon: string;
	};
};

type EnergyWidgetRegistry = {
	[T in DashEnergyWidgetTypes]: EnergyWidgetRegistryEntry<T>;
};

/**
 * Authoritative metadata for persisted energy widget types.
 *
 * Rendering components and add-gallery variants remain at their UI boundaries, while defaults,
 * titles, grid behavior, and configuration families are defined exactly once here.
 */
export const ENERGY_WIDGET_REGISTRY = {
	dashEnergyInfo: {
		titleKey: 'dash_energy_info_widget_proposal',
		titleDefault: 'Info widget',
		defaultConfig: {
			period: 'live',
			infoCategories: ['load'],
			showTariffBar: true,
		},
		grid: 'full',
		configuration: 'info',
		settings: {
			titleKey: 'dash_energy_settings_info_title',
			titleDefault: 'Shape your energy overview',
			descriptionKey: 'dash_energy_settings_info_description',
			descriptionDefault: 'Choose how the selected energy facts are calculated and presented.',
			icon: 'fal fa-grid-2',
		},
	},
	dashEnergyKpiLive: {
		titleKey: 'dash_energy_kpi_live_proposal',
		titleDefault: 'Live power',
		defaultConfig: { period: 'live' },
		grid: 'half',
		configuration: 'live',
	},
	dashEnergyKpiPeriod: {
		titleKey: 'dash_energy_kpi_period_proposal',
		titleDefault: 'Period summary',
		defaultConfig: { period: '24_hours', showTariffBar: true },
		grid: 'half',
		configuration: 'period',
		supportsPeriod: true,
	},
	dashEnergyTopConsumers: {
		titleKey: 'energy_monitoring_top_consumers_title',
		titleDefault: 'Top consumers',
		defaultConfig: { period: 'live', limit: 5, groupBy: 'device' },
		grid: 'full',
		configuration: 'ranking',
		settings: {
			titleKey: 'dash_energy_settings_top_title',
			titleDefault: 'Rank your biggest loads',
			descriptionKey: 'dash_energy_settings_top_description',
			descriptionDefault:
				'Choose whether the five largest live consumers are grouped by device or room.',
			icon: 'fal fa-ranking-star',
		},
	},
	dashEnergyConsumerMix: {
		titleKey: 'energy_monitoring_consumer_mix_title',
		titleDefault: 'Consumer mix',
		defaultConfig: { period: 'live', minSharePercent: 3, showLegend: true },
		grid: 'full',
		configuration: 'mix',
		settings: {
			titleKey: 'dash_energy_settings_mix_title',
			titleDefault: 'Tune the consumption mix',
			descriptionKey: 'dash_energy_settings_mix_description',
			descriptionDefault:
				'Reduce visual noise by grouping very small consumers and choose whether to show the legend.',
			icon: 'fal fa-chart-pie-simple',
		},
	},
	dashEnergyRoomChart: {
		titleKey: 'dash_energy_room_chart_proposal',
		titleDefault: 'Room consumption',
		defaultConfig: { period: '7_days', chartType: 'bar' },
		grid: 'full',
		configuration: 'room',
		supportsPeriod: true,
		settings: {
			titleKey: 'dash_energy_settings_room_title',
			titleDefault: 'Configure the room chart',
			descriptionKey: 'dash_energy_settings_room_description',
			descriptionDefault:
				'Choose the history range and the chart style used for the selected room.',
			icon: 'fal fa-chart-column',
		},
	},
	dashEnergyFlow: {
		titleKey: 'energy_monitoring_flow_title',
		titleDefault: 'Energy flow',
		defaultConfig: {
			period: 'live',
			showSupply: true,
			supplyMode: 'auto',
			showRooms: true,
			showDevices: true,
			maxRoomNodes: 6,
			maxDeviceNodes: 8,
		},
		grid: 'full',
		configuration: 'flow',
		settings: {
			titleKey: 'dash_energy_settings_flow_title',
			titleDefault: 'Build your live energy flow',
			descriptionKey: 'dash_energy_settings_flow_description',
			descriptionDefault:
				'Choose where supply comes from and which rooms and devices appear in the flow.',
			icon: 'fal fa-diagram-sankey',
		},
	},
	dashEnergyHeatmap: {
		titleKey: 'dash_energy_heatmap_title',
		titleDefault: 'Yearly Heatmap',
		defaultConfig: {
			period: 'live',
			heatmapScope: 'all',
			heatmapMetric: 'consumption',
			heatmapVariant: 'full',
			allowInWidgetMetricToggle: true,
			showPeriodTotal: true,
		},
		grid: 'full',
		configuration: 'heatmap',
		settings: {
			titleKey: 'dash_energy_settings_heatmap_title',
			titleDefault: 'Configure the heatmap',
			descriptionKey: 'dash_energy_settings_heatmap_description',
			descriptionDefault:
				'Set the default energy metric and decide which controls and totals remain visible.',
			icon: 'fal fa-grid',
		},
	},
	dashEnergyPeak: {
		titleKey: 'energy_monitoring_peak_overview_title',
		titleDefault: 'Peak Power Overview',
		defaultConfig: { period: 'live', peakVariant: 'overview' },
		grid: 'full',
		configuration: 'peak',
		settings: {
			titleKey: 'dash_energy_settings_peak_title',
			titleDefault: 'Choose a peak-power view',
			descriptionKey: 'dash_energy_settings_peak_description',
			descriptionDefault: 'Select the live layout that best matches your meters and phases.',
			icon: 'fal fa-bolt',
		},
	},
	dashEnergyInsights: {
		titleKey: 'settings_title_insights',
		titleDefault: 'Live insights',
		defaultConfig: { period: 'live' },
		grid: 'full',
		configuration: 'basic',
	},
	dashEnergyGeneration: {
		titleKey: 'energy_monitoring_generation_title',
		titleDefault: 'Generation vs consumption',
		defaultConfig: { period: '24_hours' },
		grid: 'full',
		configuration: 'basic',
		supportsPeriod: true,
		settings: {
			titleKey: 'dash_energy_settings_generation_title',
			titleDefault: 'Compare generation and use',
			descriptionKey: 'dash_energy_settings_generation_description',
			descriptionDefault:
				'Choose the historical window used to compare produced and consumed energy.',
			icon: 'fal fa-chart-mixed',
		},
	},
	dashEnergyPvDistribution: {
		titleKey: 'energy_monitoring_distribution_title',
		titleDefault: 'Live energy distribution',
		defaultConfig: { period: 'live' },
		grid: 'full',
		configuration: 'basic',
	},
	dashEnergyTariff: {
		titleKey: 'energy_monitoring_tariff_title',
		titleDefault: 'Tariff pressure',
		defaultConfig: { period: '24_hours' },
		grid: 'full',
		configuration: 'basic',
		supportsPeriod: true,
		settings: {
			titleKey: 'dash_energy_settings_tariff_title',
			titleDefault: 'Review tariff exposure',
			descriptionKey: 'dash_energy_settings_tariff_description',
			descriptionDefault: 'Choose the period used to calculate tariff share and estimated cost.',
			icon: 'fal fa-coins',
		},
	},
	dashEnergyPhase: {
		titleKey: 'energy_monitoring_phase_health_title',
		titleDefault: 'Phase health',
		defaultConfig: { period: 'live' },
		grid: 'full',
		configuration: 'basic',
	},
	dashEnergyEnvironmental: {
		titleKey: 'energy_monitoring_environment_title',
		titleDefault: 'Environmental conditions',
		defaultConfig: { period: 'live' },
		grid: 'full',
		configuration: 'basic',
	},
	dashEnergyDeviceTrend: {
		titleKey: 'energy_v3_device_trend_title',
		titleDefault: 'Device energy trend',
		defaultConfig: { period: '24_hours' },
		grid: 'full',
		configuration: 'device',
		supportsPeriod: true,
		settings: {
			titleKey: 'dash_energy_settings_device_trend_title',
			titleDefault: 'Follow a device over time',
			descriptionKey: 'dash_energy_settings_device_trend_description',
			descriptionDefault: 'Select a power-meter device and the history range shown in its trend.',
			icon: 'fal fa-chart-line',
		},
	},
	dashEnergyRoomCharts: {
		titleKey: 'energy_monitoring_room_chart_title',
		titleDefault: 'Room consumption',
		defaultConfig: { period: '7_days' },
		grid: 'full',
		configuration: 'rooms',
		supportsPeriod: true,
		settings: {
			titleKey: 'dash_energy_settings_rooms_title',
			titleDefault: 'Focus the room overview',
			descriptionKey: 'dash_energy_settings_rooms_description',
			descriptionDefault:
				'Choose the history range and whether to show all monitored rooms or one room.',
			icon: 'fal fa-door-open',
		},
	},
	dashEnergyRoomDistribution: {
		titleKey: 'energy_monitoring_room_distribution_title',
		titleDefault: 'Energy Distribution By Room',
		defaultConfig: { period: '7_days', showZeroConsumptionRooms: false },
		grid: 'full',
		configuration: 'basic',
		supportsPeriod: true,
		settings: {
			titleKey: 'dash_energy_settings_room_distribution_title',
			titleDefault: 'Compare room consumption',
			descriptionKey: 'dash_energy_settings_room_distribution_description',
			descriptionDefault:
				'Choose the time range used to total and compare energy consumption across every room.',
			icon: 'fal fa-chart-bar',
		},
	},
} satisfies EnergyWidgetRegistry;

export const getEnergyWidgetDefinition = <T extends DashEnergyWidgetTypes>(type: T) =>
	ENERGY_WIDGET_REGISTRY[type] as EnergyWidgetRegistryEntry<T>;
